import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { loginRequest, registerRequest } from "@docmind/types";
import { ApiError } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";

type Mode = "login" | "register";

const COPY = {
  login: {
    title: "Sign in",
    action: "Sign in",
    switchText: "No account yet?",
    switchTo: "/register",
    switchLabel: "Create one",
  },
  register: {
    title: "Create an account",
    action: "Create account",
    switchText: "Already have an account?",
    switchTo: "/login",
    switchLabel: "Sign in",
  },
} as const;

export function AuthForm({ mode }: { mode: Mode }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const copy = COPY[mode];

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    /**
     * Validated with the SAME zod schema the server enforces, imported from
     * @docmind/types. This is the concrete payoff of the monorepo (D-1): the
     * password rule lives in exactly one place, so the client cannot drift
     * into accepting something the server will reject. In a polyrepo this
     * would be a published package to version on every change.
     */
    const schema = mode === "login" ? loginRequest : registerRequest;
    const parsed = schema.safeParse({ email, password });

    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (typeof field === "string" && !errors[field]) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    setBusy(true);
    try {
      await (mode === "login"
        ? login(parsed.data.email, parsed.data.password)
        : register(parsed.data.email, parsed.data.password));

      // Back to wherever RequireAuth intercepted them.
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? "/documents", { replace: true });
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : "Something went wrong.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="centered">
      <form className="card auth-form" onSubmit={onSubmit} noValidate>
        <h1>{copy.title}</h1>

        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={Boolean(fieldErrors["email"])}
          disabled={busy}
        />
        {fieldErrors["email"] && (
          <p className="field-error">{fieldErrors["email"]}</p>
        )}

        <label htmlFor="password">Password</label>
        <input
          id="password"
          type="password"
          autoComplete={mode === "login" ? "current-password" : "new-password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          aria-invalid={Boolean(fieldErrors["password"])}
          disabled={busy}
        />
        {fieldErrors["password"] && (
          <p className="field-error">{fieldErrors["password"]}</p>
        )}
        {mode === "register" && !fieldErrors["password"] && (
          <p className="hint">At least 10 characters.</p>
        )}

        {formError && (
          <p className="form-error" role="alert">
            {formError}
          </p>
        )}

        <button type="submit" disabled={busy}>
          {busy ? "Working…" : copy.action}
        </button>

        <p className="switch">
          {copy.switchText} <Link to={copy.switchTo}>{copy.switchLabel}</Link>
        </p>
      </form>
    </div>
  );
}
